import React, { useState, useEffect } from 'react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [consent, setConsent] = useState(null); // null, 'accepted', 'rejected', or object with granular choices

  useEffect(() => {
    const storedConsent = localStorage.getItem('cookieConsent');
    if (storedConsent) {
      setConsent(JSON.parse(storedConsent));
    } else {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const newConsent = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    setConsent(newConsent);
    localStorage.setItem('cookieConsent', JSON.stringify(newConsent));
    setShowBanner(false);
    setShowPreferences(false);
    // TODO: Load third-party scripts here
  };

  const handleRejectAll = () => {
    const newConsent = {
      essential: true, // Essential cookies are always true
      analytics: false,
      marketing: false,
    };
    setConsent(newConsent);
    localStorage.setItem('cookieConsent', JSON.stringify(newConsent));
    setShowBanner(false);
    setShowPreferences(false);
    // TODO: Block third-party scripts here
  };

  const handleSavePreferences = (preferences) => {
    const newConsent = {
      essential: true,
      ...preferences,
    };
    setConsent(newConsent);
    localStorage.setItem('cookieConsent', JSON.stringify(newConsent));
    setShowBanner(false);
    setShowPreferences(false);
    // TODO: Conditionally load/block third-party scripts based on preferences
  };

  if (!showBanner && !showPreferences) {
    return null;
  }

  return (
    <>
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-8 flex items-center justify-between shadow-lg z-50">
          <p className="text-lg mr-4 flex-grow">
            This site uses cookies. Visit our{' '}
            <a href="/privacy-policy" className="underline">
              cookies policy page
            </a>{' '}
            or click the link in any footer for more information and to change your preferences.
          </p>
          <div className="flex space-x-2 flex-shrink-0">
            <button
              onClick={handleAcceptAll}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Accept All
            </button>
            <button
              onClick={handleRejectAll}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Reject All
            </button>
            <button
              onClick={() => {
                setShowBanner(false);
                setShowPreferences(true);
              }}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Manage Preferences
            </button>
          </div>
        </div>
      )}

      {showPreferences && (
        <CookiePreferencesModal
          onSave={handleSavePreferences}
          onClose={() => setShowPreferences(false)}
          initialConsent={consent}
        />
      )}
    </>
  );
};

const CookiePreferencesModal = ({ onSave, onClose, initialConsent }) => {
  const [preferences, setPreferences] = useState(
    initialConsent || {
      essential: true,
      analytics: false,
      marketing: false,
    }
  );

  const handleChange = (category) => {
    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Manage Cookie Preferences</h2>
        <p className="text-sm text-gray-700 mb-4">
          You can choose which types of cookies you'd like to accept. Essential cookies are necessary for the website to function and cannot be disabled.
        </p>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Essential Cookies</h3>
              <p className="text-xs text-gray-500">These cookies are essential for the website to function correctly.</p>
            </div>
            <span className="text-green-600 font-medium">Always On</span>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Analytics Cookies</h3>
              <p className="text-xs text-gray-500">These cookies help us understand how visitors interact with our website.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={preferences.analytics}
                onChange={() => handleChange('analytics')}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Marketing Cookies</h3>
              <p className="text-xs text-gray-500">These cookies are used to deliver relevant advertisements to you.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={preferences.marketing}
                onChange={() => handleChange('marketing')}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(preferences)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
